import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API_BASE } from './config';
import { createMonitorSocket } from '../socket/socket';
import '../css/PlayerHistory.css';

const RESULT_LABELS = {
  WIN: 'ชนะ',
  DRAW: 'เสมอ',
  LOSS: 'แพ้',
  PENDING: 'กำลังเล่น'
};

const formatDateTime = value => {
  if (!value) return '-';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '-';
  return new Intl.DateTimeFormat('th-TH', {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(date);
};

const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null');
  } catch {
    return null;
  }
};

const getAvatarSource = avatarUrl => {
  if (!avatarUrl) return '';
  if (/^https?:\/\//i.test(avatarUrl) || avatarUrl.startsWith('data:')) {
    return avatarUrl;
  }
  return `${API_BASE}${avatarUrl.startsWith('/') ? '' : '/'}${avatarUrl}`;
};

const resizeAvatar = file => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(new Error('อ่านไฟล์รูปไม่สำเร็จ'));
  reader.onload = () => {
    const image = new Image();
    image.onerror = () => reject(new Error('ไฟล์นี้ไม่ใช่รูปที่รองรับ'));
    image.onload = () => {
      const size = 512;
      const sourceSize = Math.min(image.naturalWidth, image.naturalHeight);
      const sourceX = (image.naturalWidth - sourceSize) / 2;
      const sourceY = (image.naturalHeight - sourceSize) / 2;
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const context = canvas.getContext('2d');
      context.fillStyle = '#fff7fb';
      context.fillRect(0, 0, size, size);
      context.drawImage(
        image,
        sourceX,
        sourceY,
        sourceSize,
        sourceSize,
        0,
        0,
        size,
        size
      );
      resolve(canvas.toDataURL('image/jpeg', 0.84));
    };
    image.src = reader.result;
  };
  reader.readAsDataURL(file);
});

function PlayerHistory() {
  const { groupId, userId } = useParams();
  const navigate = useNavigate();
  const selectedGroupId = Number(groupId);
  const selectedUserId = Number(userId);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarMessage, setAvatarMessage] = useState('');
  const avatarInputRef = useRef(null);
  const signedInUser = getStoredUser();
  const canEditAvatar = Boolean(
    localStorage.getItem('token') &&
    (
      Number(signedInUser?.user_id) === selectedUserId ||
      String(signedInUser?.role || '').toLowerCase() === 'admin'
    )
  );

  const loadHistory = useCallback(async ({ silent = false } = {}) => {
    if (!selectedUserId) return;
    if (silent) setRefreshing(true);
    else setLoading(true);

    try {
      const response = await fetch(
        `${API_BASE}/api/game-match/player-history/${selectedUserId}`
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || `HTTP ${response.status}`);
      }
      setHistory(data);
      setError('');
    } catch (loadError) {
      setError(loadError.message || 'โหลดประวัติผู้เล่นไม่สำเร็จ');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedUserId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    if (!selectedGroupId) return undefined;
    const monitorSocket = createMonitorSocket();
    const joinGroup = () => monitorSocket.emit('join-group', selectedGroupId);
    const refresh = payload => {
      const eventGroupId = payload?.groupId ?? payload?.group_id;
      if (eventGroupId && Number(eventGroupId) !== selectedGroupId) return;
      loadHistory({ silent: true });
    };
    const events = [
      'match-started',
      'match-ended',
      'match-updated',
      'player-status-updated'
    ];

    monitorSocket.on('connect', joinGroup);
    events.forEach(eventName => monitorSocket.on(eventName, refresh));
    if (monitorSocket.connected) joinGroup();

    return () => {
      monitorSocket.off('connect', joinGroup);
      events.forEach(eventName => monitorSocket.off(eventName, refresh));
      monitorSocket.disconnect();
    };
  }, [loadHistory, selectedGroupId]);

  useEffect(() => {
    const intervalId = window.setInterval(
      () => loadHistory({ silent: true }),
      30000
    );
    return () => window.clearInterval(intervalId);
  }, [loadHistory]);

  const handleAvatarChange = async event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setAvatarMessage('กรุณาเลือกไฟล์รูปภาพ');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setAvatarMessage('รูปต้นฉบับต้องมีขนาดไม่เกิน 10 MB');
      return;
    }

    try {
      setUploadingAvatar(true);
      setAvatarMessage('');
      const dataUrl = await resizeAvatar(file);
      const response = await fetch(`${API_BASE}/api/users/${selectedUserId}/avatar`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ dataUrl })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'อัปโหลดรูปไม่สำเร็จ');
      }
      setHistory(current => ({
        ...current,
        player: { ...current.player, avatarUrl: data.avatarUrl }
      }));
      setAvatarMessage('เพิ่มรูปผู้เล่นแล้ว ✨');
    } catch (uploadError) {
      setAvatarMessage(uploadError.message || 'อัปโหลดรูปไม่สำเร็จ');
    } finally {
      setUploadingAvatar(false);
    }
  };

  if (loading) {
    return <main className="player-history-page loading">กำลังโหลดประวัติผู้เล่น...</main>;
  }

  return (
    <main className="player-history-page">
      <header className="player-history-hero">
        <div className="player-history-profile">
          <div className="player-avatar-wrap">
            <div className="player-history-avatar avatar-placeholder" aria-hidden={!history?.player?.avatarUrl}>
              {history?.player?.name?.trim()?.charAt(0) || '🏸'}
              {history?.player?.avatarUrl && (
              <img
                className="player-history-avatar-image"
                src={getAvatarSource(history.player.avatarUrl)}
                alt={`รูปของ ${history.player.name}`}
                onError={event => { event.currentTarget.style.display = 'none'; }}
              />
              )}
            </div>
            <span className="avatar-sparkle" aria-hidden="true">✦</span>
            {canEditAvatar && (
              <>
                <input
                  ref={avatarInputRef}
                  className="avatar-file-input"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleAvatarChange}
                />
                <button
                  type="button"
                  className="avatar-upload-button"
                  disabled={uploadingAvatar}
                  onClick={() => avatarInputRef.current?.click()}
                >
                  {uploadingAvatar ? '…' : '📷'}
                  <span>{uploadingAvatar ? 'กำลังเพิ่มรูป' : 'เพิ่มรูป'}</span>
                </button>
              </>
            )}
          </div>
          <div className="player-history-name">
            <span>PLAYER MATCH HISTORY</span>
            <h1>{history?.player?.name || 'ประวัติผู้เล่น'}</h1>
            <p>
              มือ {history?.player?.levelRank || '-'} • {history?.player?.point || 0} คะแนน
            </p>
            {avatarMessage && <small className="avatar-message">{avatarMessage}</small>}
          </div>
        </div>
        <nav>
          <button type="button" onClick={() => navigate(`/monitor/${selectedGroupId}`)}>
            ← กลับ Monitor Match
          </button>
          <button
            type="button"
            className="history-refresh"
            disabled={refreshing}
            onClick={() => loadHistory({ silent: true })}
          >
            {refreshing ? 'กำลังอัปเดต...' : '↻ อัปเดต'}
          </button>
        </nav>
      </header>

      {error && <div className="player-history-error">{error}</div>}

      {history && (
        <>
          <section className="history-summary-grid" aria-label="สรุปผลการแข่งขัน">
            <article className="overall"><span>💖 Win rate รวม</span><strong>{history.summary.winRate}%</strong></article>
            <article className="completed"><span>🏸 เล่นจบ</span><strong>{history.summary.completedMatches}</strong></article>
            <article className="win"><span>🌟 ชนะ</span><strong>{history.summary.wins}</strong></article>
            <article className="draw"><span>🤝 เสมอ</span><strong>{history.summary.draws}</strong></article>
            <article className="loss"><span>🌈 แพ้</span><strong>{history.summary.losses}</strong></article>
          </section>

          <section className="history-section">
            <header>
              <div>
                <h2>💐 Win rate แยกตาม Group</h2>
                <p>นับเฉพาะ Match ที่บันทึกผลแล้ว</p>
              </div>
            </header>
            <div className="history-group-grid">
              {history.groups.length > 0 ? history.groups.map(group => (
                <article key={group.groupId}>
                  <span>{group.groupName}</span>
                  <strong>{group.winRate}%</strong>
                  <div className="history-rate-track">
                    <i style={{ width: `${Math.min(100, group.winRate)}%` }} />
                  </div>
                  <small>
                    {group.matches} เกม • ชนะ {group.wins} • เสมอ {group.draws} • แพ้ {group.losses}
                  </small>
                </article>
              )) : <div className="history-empty">ยังไม่มีผลการแข่งขัน</div>}
            </div>
          </section>

          <section className="history-section">
            <header>
              <div>
                <h2>🎀 รายละเอียดแต่ละ Match</h2>
                <p>ทีมเดียวกัน คู่แข่ง สนาม และผลการแข่งขัน</p>
              </div>
            </header>
            <div className="history-match-list">
              {history.matches.length > 0 ? history.matches.map(match => (
                <article className={`history-match result-${match.result.toLowerCase()}`} key={match.matchId}>
                  <div className="history-match-meta">
                    <span>{match.groupName}</span>
                    <strong>
                      Match #{match.groupMatchNo || match.matchId} • Court {match.courtNo || '-'}
                    </strong>
                    <small>{formatDateTime(match.playedAt)}</small>
                  </div>
                  <b className="history-result">{RESULT_LABELS[match.result]}</b>
                  <div className="history-people">
                    <span>
                      คู่ทีม
                      <strong>{match.teammates.map(person => person.name).join(', ') || '-'}</strong>
                    </span>
                    <span>
                      คู่แข่ง
                      <strong>{match.opponents.map(person => person.name).join(', ') || '-'}</strong>
                    </span>
                  </div>
                </article>
              )) : <div className="history-empty">ยังไม่มีประวัติ Match</div>}
            </div>
          </section>
        </>
      )}
    </main>
  );
}

export default PlayerHistory;
